import { Button } from '@consta/uikit/Button'
import { Checkbox } from '@consta/uikit/Checkbox'
import { ChoiceGroup } from '@consta/uikit/ChoiceGroup'
import { Radio } from '@consta/uikit/Radio'
import { Select } from '@consta/uikit/Select'
import { Text } from '@consta/uikit/Text'
import type { TextFieldPropOnChange } from '@consta/uikit/TextField'
import { TextField } from '@consta/uikit/TextField'
import { isMultipartSetupOfSpecificType } from '@core/core/store'
import type {
  SegyHeaderCoordsType,
  SegySampleParamsInfo,
  SegySetupType,
  SurveyType,
} from '@core/core/types'
import cn from 'classnames'
import type { CommonDomainType, Keys, Nullable } from 'common/types'
import type { ChangeEventHandler, FC } from 'react'
import { useLayoutEffect, useState } from 'react'

import {
  coordinates,
  coordTemplateTypes,
  domainChoiceItems,
  formatItems,
  headerChoiceItems,
  optionHeaderTypes,
  requiredOptions,
  typeChoiceItems,
} from '../../MasterImportData'
import type { ChoiceItemType, OptionHeaderType } from '../../types'
import type { UseSegyParserType } from '../segyHelpers/'
import { useSegyParser } from '../segyHelpers/'
import { BinaryHeaderTable } from './BinaryHeaderTable'
import { CoordinatesFields } from './CoordinatesFields'
import { OptionsForm } from './OptionsForm'
import css from './Setup.css'
import { TextualHeaderTable } from './TextualHeaderTable'
import { TraceHeaderTable } from './TraceHeaderTable'
import type { SetupContent, SetupProps } from './types'

export const Setup: FC<SetupProps> = ({ file, updateFileConfig }) => {
  const [headerChoiceValue, setHeaderChoiceValue] = useState<{ name: string }>(headerChoiceItems[0])

  const [setupContent, setSetupContent] = useState<SetupContent>({
    surveyName: '',
    surveyType: 'Survey2D',
    domainType: 'Z',
    ignoreSegy: false,
    check: false,
    ignoreZeros: false,
    isValid: false,
    ...file?.uploadedFile?.setupContent,
    changed: false,
  })
  const [parseResult, setParseResult] = useState<UseSegyParserType>({
    textualHeader: undefined,
    binaryHeader: undefined,
    traceHeaders: undefined,
    samplingStep: undefined,
    samplesNumber: undefined,
    formatCode: undefined,
    headersCoords: undefined,
    delayRecordingTime: undefined,
  })

  const uploadedFile = file?.uploadedFile
  const setupContentProp = isMultipartSetupOfSpecificType<SegySetupType>(
    uploadedFile,
    uploadedFile?.setupContent,
    'segy',
  )
    ? uploadedFile.setupContent
    : undefined

  const onParseResult = (result: UseSegyParserType) => {
    const getSetupContent = (previousSetupContent: SetupContent) => {
      const samples = { ...previousSetupContent.samples }
      const headerCoords = { ...previousSetupContent.headerCoords }
      const resultHeaderCoords = structuredClone(result.headersCoords)
      if (
        resultHeaderCoords?.x?.offset &&
        resultHeaderCoords.y?.offset &&
        resultHeaderCoords.iline?.offset &&
        resultHeaderCoords.xline?.offset
      ) {
        ++resultHeaderCoords.x.offset
        ++resultHeaderCoords.y.offset
        ++resultHeaderCoords.iline.offset
        ++resultHeaderCoords.xline.offset
      }

      if (
        samples.interval !== result.samplingStep ||
        samples.samplesPerTrace !== result.samplesNumber ||
        samples.samplesFormat !== result.formatCode ||
        samples.startRecord !== result.delayRecordingTime ||
        headerCoords.x?.offset !== resultHeaderCoords?.x?.offset ||
        headerCoords.y?.offset !== resultHeaderCoords?.y?.offset ||
        headerCoords.iline?.offset !== resultHeaderCoords?.iline?.offset ||
        headerCoords.xline?.offset !== resultHeaderCoords?.xline?.offset
      ) {
        const delayRecordingTime = result.delayRecordingTime ?? 0
        samples.startRecord ??= -delayRecordingTime
        // В segy сохраняется delayRecordingTime и оно имеет обратный знак к значению "Начало времени записи"
        // Поэтому пользователю будет отображаться обратное значение
        samples.interval ??= result.samplingStep
        samples.samplesPerTrace ??= result.samplesNumber
        samples.samplesFormat ??= result.formatCode

        return {
          ...previousSetupContent,
          samples,
          headerCoords: previousSetupContent.headerCoords ?? resultHeaderCoords,
          changed: true,
        }
      }

      return previousSetupContent
    }
    setParseResult(result)
    setSetupContent(getSetupContent)
    // if (file?.fileId) {
    //   dispatch(
    //     setMultipartInitialSetup({
    //       fileId: file.fileId,
    //       fileSetupType: initialSetupFromFile,
    //     }),
    //   )
    // }
  }

  useSegyParser(file?.file, onParseResult)

  useLayoutEffect(() => {
    if (setupContentProp) {
      const newSetupContent = { ...setupContentProp, changed: false }
      setSetupContent(newSetupContent)
    }
  }, [setupContentProp])

  useLayoutEffect(() => {
    if (setupContent.changed && file?.fileId) {
      updateFileConfig(file.fileId, setupContent)
    }
  }, [setupContent])

  const onFileNameChange: TextFieldPropOnChange = value => {
    setSetupContent(prev => ({
      ...prev,
      surveyName: value ?? '',
      changed: true,
    }))
  }
  const setDomainChoiceValue = (value: ChoiceItemType<CommonDomainType>) => {
    setSetupContent(prev => ({
      ...prev,
      domainType: value.name,
      changed: true,
    }))
  }

  const setFormatValue = (value: Nullable<OptionHeaderType>) => {
    if (!value) return

    const samples: SegySampleParamsInfo = {
      ...setupContent.samples,
      samplesFormat: value.name,
    }
    setSamplesValue(samples)
  }

  const setHeaderCoordsValue = (headerCoords: SegyHeaderCoordsType) => {
    setSetupContent(prev => ({
      ...prev,
      headerCoords,
      changed: true,
    }))
  }

  const setSamplesValue = (samples: SegySampleParamsInfo) => {
    setSetupContent(prev => ({
      ...prev,
      samples,
      changed: true,
    }))
  }

  const setIgnoreZerosValue: ChangeEventHandler<HTMLInputElement> = event => {
    setSetupContent(prev => ({
      ...prev,
      ignoreZeros: event.target.checked,
      changed: true,
    }))
  }
  const setTypeChoiceValue = (value: ChoiceItemType<SurveyType>) => {
    setSetupContent(prev => ({
      ...prev,
      surveyType: value.name,
      changed: true,
    }))
  }
  const onSelectTemplate = (value: SegyHeaderCoordsType) => {
    let newValue = value
    for (const coord in value) {
      newValue = {
        ...newValue,
        [coord]: {
          offset: value[coord as Keys<SegyHeaderCoordsType>]?.offset,
          type: setupContent.samples?.samplesFormat,
        },
      }
    }
    setSetupContent(prev => ({
      ...prev,
      headerCoords: newValue,
      changed: true,
    }))
  }

  const setupClassName = cn(css.setupPadding, css.setupDirection, css.setupAutoScroll)
  const headerChoiceGroupClassName = cn(css.choiceGroupHeaders, css.headerMargin)

  const getCordTemplateLabel = (template: SegyHeaderCoordsType) => {
    let label = ''
    for (const prop in template) {
      label += (!label ? '' : '-') + template[prop as Keys<SegyHeaderCoordsType>]?.offset
    }
    return label
  }

  const samplesFormat = setupContent.samples?.samplesFormat
  return (
    <div className={setupClassName}>
      <div className={`${css.setupScroll} ${css.setupWidth} ${css.setupPadding}`}>
        <div>
          <div className={css.surveyWrapper}>
            <Radio label="Название съемки" checked={true} className={css.radioLabel} />
            <TextField
              value={setupContent.surveyName}
              size="s"
              status={!setupContent.surveyName.trim() ? 'alert' : undefined}
              onChange={onFileNameChange}
            />
          </div>
          <div>
            <Radio
              label="Выбрать съемку для загрузки"
              checked={setupContent.check}
              className={css.radioLabel}
            />
            <Select
              size="s"
              items={formatItems}
              getItemKey={item => item.label}
              placeholder="Выбрать"
              onChange={() => {
                console.log('not implemented yet')
              }}
            />
          </div>
        </div>
        <div className={`${css.fullWidth} ${css.typeChoiceContainer}`}>
          <div className={`${css.halfWidth} ${css.typeChoiceWrapper}`}>
            <Text size="s" view="secondary" className={css.textPadding}>
              Тип
            </Text>
            <ChoiceGroup
              name="typeChoiceValue"
              size="s"
              view="ghost"
              width="full"
              items={typeChoiceItems}
              value={typeChoiceItems.find(item => item.name === setupContent.surveyType)}
              getItemLabel={item => item.label}
              getItemDisabled={item => item.disabled}
              multiple={false}
              onChange={setTypeChoiceValue}
            />
          </div>
          <div className={css.halfWidth}>
            <Text size="s" view="secondary" className={css.textPadding}>
              Домен
            </Text>
            <ChoiceGroup
              name="domainChoiceValue"
              size="s"
              view="ghost"
              width="full"
              items={domainChoiceItems}
              value={domainChoiceItems.find(item => item.name === setupContent.domainType)}
              getItemLabel={item => item.label}
              multiple={false}
              onChange={setDomainChoiceValue}
            />
          </div>
        </div>
        <div className={css.checkboxWrapper}>
          <Checkbox
            label="Не учитывать SGY координаты"
            checked={setupContent.ignoreSegy}
            disabled={true}
            className={`${css.checkboxLabel} ${css.ignoreSegy}`}
          />
          <Checkbox
            label="Не учитывать трассы с координатами (0;0)"
            checked={setupContent.ignoreZeros}
            className={`${css.checkboxLabel} ${css.ignoreZeros}`}
            onChange={setIgnoreZerosValue}
          />
        </div>
        <div className={css.coordinatesWrapper}>
          <Text size="s" view="secondary" className={css.coordinatesLabel}>
            Координаты
          </Text>
          {coordTemplateTypes.map((item, i) => (
            <Button
              key={i}
              size="xs"
              view="ghost"
              label={getCordTemplateLabel(item)}
              className={css.coordinatesBtn}
              onClick={() => {
                onSelectTemplate(item)
              }}
            />
          ))}
        </div>
        <div className={css.coordinatesList}>
          {coordinates.map(item => (
            <CoordinatesFields
              key={item.name}
              coordProps={item}
              coordValue={setupContent.headerCoords}
              isPadding={item.isPadding}
              setHeaderCoordsValue={setHeaderCoordsValue}
            />
          ))}
        </div>
        <Text size="s" view="secondary" className={css.formatText}>
          Формат
        </Text>
        <Select
          items={optionHeaderTypes}
          value={optionHeaderTypes.find(option => option.name === samplesFormat)}
          getItemKey={item => item.label}
          size="s"
          className={css.formatSelect}
          status={!samplesFormat ? 'alert' : undefined}
          onChange={setFormatValue}
        />
        <div className={css.optionsWrapper}>
          {requiredOptions.map(item => (
            <OptionsForm
              key={item.label}
              samplesProps={item}
              samplesValue={setupContent.samples}
              setSamplesValue={setSamplesValue}
            />
          ))}
        </div>
      </div>
      <div
        className={`${css.importWrapper} ${css.setupWidth} ${css.setupPadding} ${css.flexedColumn}`}>
        <Text size="s" view="secondary" className={css.importText}>
          Файл импорта
        </Text>
        <ChoiceGroup
          name="headerChoiceValue"
          size="s"
          view="ghost"
          width="full"
          truncate={true}
          className={headerChoiceGroupClassName}
          items={headerChoiceItems}
          value={headerChoiceValue}
          getItemLabel={item => item.name}
          multiple={false}
          onChange={setHeaderChoiceValue}
        />
        <div className={css.codeFrame}>
          {headerChoiceItems[0] === headerChoiceValue && (
            <TextualHeaderTable textualHeader={parseResult.textualHeader} />
          )}
          {headerChoiceItems[1] === headerChoiceValue && (
            <BinaryHeaderTable binaryHeader={parseResult.binaryHeader} />
          )}
          {headerChoiceItems[2] === headerChoiceValue && (
            <TraceHeaderTable traceHeaders={parseResult.traceHeaders} />
          )}
        </div>
      </div>
    </div>
  )
}
